/**
 * Genealogy source-of-truth extracted from editor runtime.
 * Keep this module in `src/` so compiler/runtime bundles own the behavior.
 */

export const GENEALOGY_WORKSPACE_PRESETS = [
  { libraryCategory: "profiles", label: "Male", type: "person", shape: "rect", previewShape: "gene-male", tooltip: "male-profile", genealogyRole: "male", color: "#6ea8fe", width: 160, height: 108 },
  { libraryCategory: "profiles", label: "Female", type: "person", shape: "circle", previewShape: "gene-female", tooltip: "female-profile", genealogyRole: "female", color: "#f472b6", width: 160, height: 108 },
  { libraryCategory: "connectors", label: "Parent link", type: "custom", shape: "arrow-down", previewShape: "gene-link-v", tooltip: "parent-link", genealogyRole: "link-v", color: "#cbd5e1", width: 72, height: 128 },
  { libraryCategory: "connectors", label: "Relational connector", type: "custom", shape: "rect", previewShape: "gene-link-h", tooltip: "spouse-link", genealogyRole: "link-h", color: "#94a3b8", width: 128, height: 64 },
  { libraryCategory: "connectors", label: "Union anchor", type: "custom", shape: "rect", previewShape: "gene-union", tooltip: "union-node", genealogyRole: "union", color: "transparent", width: 4, height: 4 },
];

export const GENEALOGY_SIDEBAR_TILE_SVG = {
  "gene-male":
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false" stroke="#ffffff" stroke-width="2" fill="none" style="width:18px;height:18px;display:block;flex-shrink:0"><rect x="5" y="5" width="14" height="14" rx="1.5"/><circle cx="12" cy="10" r="2.5" fill="#6EA8FE" stroke="#ffffff" stroke-width="1.5"/><path d="M8 18c.7-2.2 2-3.5 4-3.5s3.3 1.3 4 3.5"/></svg>',
  "gene-female":
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false" stroke="#ffffff" stroke-width="2" fill="none" style="width:18px;height:18px;display:block;flex-shrink:0"><ellipse cx="12" cy="9.5" rx="4.5" ry="4.5"/><path d="M6.5 19c1-2.8 2.6-4.2 5.5-4.2s4.5 1.4 5.5 4.2"/></svg>',
  "gene-link-h":
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false" stroke="#ffffff" stroke-width="2" fill="none" style="width:18px;height:18px;display:block;flex-shrink:0"><circle cx="8" cy="12" r="4.5"/><circle cx="16" cy="12" r="4.5"/><line x1="12.5" y1="12" x2="11.5" y2="12"/></svg>',
  "gene-link-v":
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false" stroke="#ffffff" stroke-width="2" fill="none" style="width:18px;height:18px;display:block;flex-shrink:0"><line x1="12" y1="4" x2="12" y2="16"/><line x1="9" y1="16" x2="15" y2="16"/></svg>',
  "gene-union":
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false" stroke="#ffffff" stroke-width="2" fill="none" style="width:18px;height:18px;display:block;flex-shrink:0"><line x1="3" y1="12" x2="21" y2="12"/><line x1="12" y1="12" x2="12" y2="20"/><circle cx="12" cy="12" r="2" fill="#ffffff" stroke="none"/></svg>',
};

export function genealogySidebarTileSvg(preset) {
  const key = preset.previewShape || preset.genealogyRole || "";
  if (key === "male") return GENEALOGY_SIDEBAR_TILE_SVG["gene-male"];
  if (key === "female") return GENEALOGY_SIDEBAR_TILE_SVG["gene-female"];
  if (key === "link-h") return GENEALOGY_SIDEBAR_TILE_SVG["gene-link-h"];
  if (key === "link-v") return GENEALOGY_SIDEBAR_TILE_SVG["gene-link-v"];
  if (key === "union") return GENEALOGY_SIDEBAR_TILE_SVG["gene-union"];
  return GENEALOGY_SIDEBAR_TILE_SVG[key] || GENEALOGY_SIDEBAR_TILE_SVG["gene-male"];
}

export function genealogySidebarTileMarkup(preset) {
  const svg = genealogySidebarTileSvg(preset);
  const role = preset.genealogyRole;
  if (role === "male" || role === "female") {
    const tintClass = role === "female" ? "node-tile__gene-icon--female" : "node-tile__gene-icon--male";
    return `<span class="node-tile__gene-icon ${tintClass}" aria-hidden="true">${svg}</span>`;
  }
  return svg;
}

function getMarriageGeometry(a, b, getNodeWorldPosition) {
  const aw = getNodeWorldPosition(a);
  const bw = getNodeWorldPosition(b);
  const aCx = aw.x + a.width / 2;
  const bCx = bw.x + b.width / 2;
  const left = aCx <= bCx ? a : b;
  const right = left === a ? b : a;
  const lw = getNodeWorldPosition(left);
  const rw = getNodeWorldPosition(right);
  return {
    left,
    right,
    leftCy: lw.y + left.height / 2,
    rightCy: rw.y + right.height / 2,
    leftEdgeX: lw.x + left.width,
    rightEdgeX: rw.x,
  };
}

/**
 * Right-center of left partner -> Left-center of right partner.
 * Falls back to a clean orthogonal segment when vertical centers differ.
 */
export function layoutGenealogySpouseEdge(fromNode, toNode, getNodeWorldPosition) {
  const geo = getMarriageGeometry(fromNode, toNode, getNodeWorldPosition);
  const fromIsLeft = fromNode.id === geo.left.id;
  const leftAnchor = { x: geo.leftEdgeX, y: geo.leftCy, edge: "right" };
  const rightAnchor = { x: geo.rightEdgeX, y: geo.rightCy, edge: "left" };
  const from = fromIsLeft ? leftAnchor : rightAnchor;
  const to = fromIsLeft ? rightAnchor : leftAnchor;

  let d;
  if (Math.abs(leftAnchor.y - rightAnchor.y) < 2) {
    d = `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
  } else {
    const midY = (from.y + to.y) / 2;
    d = `M ${from.x} ${from.y} L ${from.x} ${midY} L ${to.x} ${midY} L ${to.x} ${to.y}`;
  }
  return { from, to, d };
}

export function layoutGenealogyNonSpouseEdge(fromNode, toNode, getNodeWorldPosition) {
  const fw = getNodeWorldPosition(fromNode);
  const tw = getNodeWorldPosition(toNode);
  const fromX = fw.x + fromNode.width / 2;
  const fromY = fw.y + fromNode.height;
  const toX = tw.x + toNode.width / 2;
  const toY = tw.y;
  const from = { x: fromX, y: fromY, edge: "bottom" };
  const to = { x: toX, y: toY, edge: "top" };
  let d;
  if (Math.abs(fromX - toX) < 1.5) {
    d = `M ${fromX} ${fromY} L ${toX} ${toY}`;
  } else {
    const midY = (fromY + toY) / 2;
    d = `M ${fromX} ${fromY} L ${fromX} ${midY} L ${toX} ${midY} L ${toX} ${toY}`;
  }
  return { from, to, d };
}

export const GENEALOGY_TOOLBAR_ICON_SVG = {
  male:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false" stroke="#ffffff" stroke-width="2" fill="none" style="width:14px;height:14px;display:block;flex-shrink:0"><rect x="6" y="6" width="12" height="12" rx="1.5"/></svg>',
  female:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false" stroke="#ffffff" stroke-width="2" fill="none" style="width:14px;height:14px;display:block;flex-shrink:0"><circle cx="12" cy="12" r="6"/></svg>',
};

export function genealogyToolbarActionButtonMarkup(role, label) {
  const safeRole = role === "female" ? "female" : "male";
  const tintClass = safeRole === "female" ? "gene-toolbar-icon--female" : "gene-toolbar-icon--male";
  const icon = GENEALOGY_TOOLBAR_ICON_SVG[safeRole];
  return `<span class="gene-toolbar-icon ${tintClass}" aria-hidden="true">${icon}</span><span class="gene-toolbar-label">${label}</span>`;
}

function ensureGenealogyToolbarCss(doc) {
  if (!doc || doc.getElementById("genealogyToolbarRuntimeStyles")) return;
  const style = doc.createElement("style");
  style.id = "genealogyToolbarRuntimeStyles";
  style.textContent = `
    .canvas-micro-toolbar__btn--gene.gene-toolbar-action{display:flex;align-items:center;gap:6px;min-width:88px;padding:0 10px}
    .canvas-micro-toolbar__btn--gene.gene-toolbar-action .gene-toolbar-label{font-size:10px;font-weight:600;letter-spacing:.02em;line-height:1}
    .canvas-micro-toolbar__btn--gene.gene-toolbar-action .gene-toolbar-icon{display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:6px;flex-shrink:0}
    .canvas-micro-toolbar__btn--gene.gene-toolbar-action .gene-toolbar-icon--male{background:color-mix(in srgb, #6ea8fe 34%, #1a2744);box-shadow:0 0 8px color-mix(in srgb, #6ea8fe 42%, transparent)}
    .canvas-micro-toolbar__btn--gene.gene-toolbar-action .gene-toolbar-icon--female{background:color-mix(in srgb, #f472b6 34%, #2a1424);box-shadow:0 0 8px color-mix(in srgb, #f472b6 42%, transparent)}
  `;
  doc.head?.appendChild(style);
}

function syncGenealogyToolbarButtons(doc, globalObj) {
  const spouseBtn = doc?.getElementById?.("geneAddSpouseBtn");
  const childBtn = doc?.getElementById?.("geneAddChildBtn");
  if (!spouseBtn || !childBtn) return;
  const isGenealogy = typeof globalObj.isGenealogyWorkspaceActive === "function" && globalObj.isGenealogyWorkspaceActive();
  if (!isGenealogy) return;

  spouseBtn.classList.add("gene-toolbar-action");
  childBtn.classList.add("gene-toolbar-action");
  spouseBtn.innerHTML = genealogyToolbarActionButtonMarkup("female", "Add Female");
  childBtn.innerHTML = genealogyToolbarActionButtonMarkup("male", "Add Male");
  spouseBtn.title = "Add Female";
  spouseBtn.setAttribute("aria-label", "Add Female");
  childBtn.title = "Add Male";
  childBtn.setAttribute("aria-label", "Add Male");

  spouseBtn.hidden = false;
  childBtn.hidden = false;
  spouseBtn.style.display = "";
  childBtn.style.display = "";
}

let _toolbarBridgeInstalled = false;
let _routingBridgeInstalled = false;
let _toolbarHandlersBound = false;

function getSelectedGenealogyNode(globalObj) {
  if (typeof globalObj.getNodeById !== "function") return null;
  const runtime = globalObj.runtime || {};
  const selectedId =
    runtime.selectedNodeId ||
    (runtime.selectedNodeIds && runtime.selectedNodeIds.size ? [...runtime.selectedNodeIds][0] : null);
  if (!selectedId) return null;
  const n = globalObj.getNodeById(selectedId);
  if (!n || !n.genealogyRole) return null;
  return n;
}

function getGenealogyPresetByRoleFromSource(globalObj, role) {
  if (typeof globalObj.getGenealogyPresetByRole === "function") {
    return globalObj.getGenealogyPresetByRole(role);
  }
  return GENEALOGY_WORKSPACE_PRESETS.find((p) => p.genealogyRole === role) || null;
}

function resolveQuickAddLinkType(globalObj, selectedNode, role) {
  const selectedRole = String(selectedNode?.genealogyRole || "");
  const isAdult = selectedRole === "male" || selectedRole === "female";
  const oppositeAdult =
    (selectedRole === "male" && role === "female") || (selectedRole === "female" && role === "male");
  if (isAdult && oppositeAdult && typeof globalObj.findGenealogySpouse === "function") {
    const spouse = globalObj.findGenealogySpouse(selectedNode.id);
    if (!spouse) return "spouse";
  }
  return "parent-child";
}

function quickAddGenealogyNode(globalObj, role) {
  if (typeof globalObj.isGenealogyWorkspaceActive === "function" && !globalObj.isGenealogyWorkspaceActive()) {
    return;
  }
  const selected = getSelectedGenealogyNode(globalObj);
  if (!selected) return;
  const preset = getGenealogyPresetByRoleFromSource(globalObj, role);
  if (!preset) return;
  const canBuild =
    typeof globalObj.buildGenealogyNodeFromPreset === "function" &&
    typeof globalObj.ensureGenealogyEdge === "function" &&
    typeof globalObj.getProject === "function";
  if (!canBuild) return;

  if (typeof globalObj.pushHistory === "function") globalObj.pushHistory();
  const p = globalObj.getProject();
  const xOffset = role === "female" ? selected.x + selected.width + 100 : selected.x - (preset.width || 160) - 100;
  const yOffset = selected.y + Math.max(44, Math.round(selected.height * 0.35));
  const added = globalObj.buildGenealogyNodeFromPreset(preset, xOffset, yOffset);
  p.nodes.push(added);
  const linkType = resolveQuickAddLinkType(globalObj, selected, role);
  globalObj.ensureGenealogyEdge(selected.id, added.id, linkType);
  if (globalObj.selectionRuntime?.selectNode) globalObj.selectionRuntime.selectNode(added.id);
  if (typeof globalObj.renderAll === "function") globalObj.renderAll();
  if (typeof globalObj.elevateSpawnedNode === "function") globalObj.elevateSpawnedNode(added.id);
  if (typeof globalObj.flashFcNodeEnter === "function") globalObj.flashFcNodeEnter(added.id);
  if (typeof globalObj.markDirty === "function") globalObj.markDirty();
}

function bindGenealogyToolbarHandlers(doc, globalObj) {
  if (_toolbarHandlersBound) return;
  const spouseBtn = doc?.getElementById?.("geneAddSpouseBtn");
  const childBtn = doc?.getElementById?.("geneAddChildBtn");
  if (!spouseBtn || !childBtn) return;

  // Replace to drop any previous listeners and bind deterministic quick-add behavior.
  const spouseClone = spouseBtn.cloneNode(true);
  const childClone = childBtn.cloneNode(true);
  spouseBtn.replaceWith(spouseClone);
  childBtn.replaceWith(childClone);

  spouseClone.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    quickAddGenealogyNode(globalObj, "female");
  });
  childClone.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    quickAddGenealogyNode(globalObj, "male");
  });
  _toolbarHandlersBound = true;
}

function installGenealogyToolbarBridge(globalObj) {
  if (_toolbarBridgeInstalled) return true;
  const doc = globalObj.document;
  if (!doc) return false;
  ensureGenealogyToolbarCss(doc);
  bindGenealogyToolbarHandlers(doc, globalObj);
  syncGenealogyToolbarButtons(doc, globalObj);
  // Avoid mutation-observer feedback loops by piggybacking on the existing toolbar sync function.
  if (
    typeof globalObj.syncQuickToolbarGenealogyVisibility === "function" &&
    !globalObj.syncQuickToolbarGenealogyVisibility.__genealogyToolbarPatched
  ) {
    const original = globalObj.syncQuickToolbarGenealogyVisibility;
    const wrapped = function wrappedSyncQuickToolbarGenealogyVisibility(...args) {
      const out = original.apply(this, args);
      bindGenealogyToolbarHandlers(doc, globalObj);
      syncGenealogyToolbarButtons(doc, globalObj);
      return out;
    };
    wrapped.__genealogyToolbarPatched = true;
    globalObj.syncQuickToolbarGenealogyVisibility = wrapped;
  }
  _toolbarBridgeInstalled = true;
  return true;
}

function installGenealogyRoutingBridge(globalObj) {
  if (_routingBridgeInstalled) return true;
  if (
    typeof globalObj.layoutGenealogyEdge !== "function" ||
    typeof globalObj.getNodeWorldPosition !== "function"
  ) {
    return false;
  }

  const getNodeWorldPosition = globalObj.getNodeWorldPosition;
  const originalResolve =
    typeof globalObj.resolveGenealogyLinkType === "function" ? globalObj.resolveGenealogyLinkType : null;

  globalObj.layoutGenealogyEdge = function patchedLayoutGenealogyEdge(fromNode, toNode, linkType) {
    if (linkType === "spouse") return layoutGenealogySpouseEdge(fromNode, toNode, getNodeWorldPosition);
    return layoutGenealogyNonSpouseEdge(fromNode, toNode, getNodeWorldPosition);
  };

  if (originalResolve) {
    globalObj.resolveGenealogyLinkType = function patchedResolveGenealogyLinkType(c) {
      const resolved = originalResolve.call(this, c);
      if (resolved === "spouse") return resolved;
      if (!c || typeof globalObj.getNodeById !== "function") return resolved;
      const fromNode = globalObj.getNodeById(c.from);
      const toNode = globalObj.getNodeById(c.to);
      if (!fromNode || !toNode) return resolved;
      const isGenealogyConnection = !!(fromNode.genealogyRole || toNode.genealogyRole);
      if (!isGenealogyConnection) return resolved;
      return resolved || "parent-child";
    };
  }

  _routingBridgeInstalled = true;
  return true;
}

export function installGenealogyRuntimeBridges(globalObj = globalThis) {
  const toolbarReady = installGenealogyToolbarBridge(globalObj);
  const routingReady = installGenealogyRoutingBridge(globalObj);
  return { toolbarReady, routingReady };
}
